import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSuperAdminAuth } from '../contexts/SuperAdminAuthContext';
import { useSuperAdminTheme } from '../contexts/SuperAdminThemeContext';
import { cn } from '../lib/utils';

/**
 * SuperAdminRoute - Enforces SEPARATE Governor authentication
 * 
 * USER HIERARCHY:
 * - Governor (role="governor") - CAN access governance routes
 * - Superadmin (role="superadmin") - CANNOT access governance routes
 * - User (role="user") - CANNOT access governance routes
 * 
 * ISOLATION REQUIREMENTS:
 * - Uses SuperAdminAuthContext (NOT regular AuthContext)
 * - Uses SuperAdminThemeContext (NOT CRM theme)
 * - Requires governance-scoped token (NOT CRM token)
 * - Redirects to /superadmin/login (NOT /login)
 * 
 * For governance routes, requires:
 * - Valid governance-scoped authentication
 * - User role is 'governor' (Lockene internal only)
 */
export const SuperAdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useSuperAdminAuth();
  const { theme, isDark } = useSuperAdminTheme();
  const location = useLocation();

  // Show loading spinner while auth is being checked
  if (loading) {
    return (
      <div 
        className={cn(
          "min-h-screen flex items-center justify-center",
          isDark ? "bg-slate-950" : "bg-slate-100"
        )} 
        data-testid="superadmin-loading"
        data-theme={theme}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className={isDark ? "text-slate-400" : "text-slate-600"}>Verifying governance access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to SUPERADMIN login (not CRM login)
  if (!isAuthenticated) {
    console.log('[SuperAdminRoute] Not authenticated, redirecting to SuperAdmin login');
    return <Navigate to="/superadmin/login" state={{ from: location.pathname }} replace />;
  }

  // Verify governance scope
  if (user?.scope !== 'governance') {
    console.log('[SuperAdminRoute] Invalid scope, requires governance token');
    return (
      <div 
        className={cn(
          "min-h-screen flex items-center justify-center",
          isDark ? "bg-slate-950" : "bg-slate-100"
        )} 
        data-testid="invalid-scope"
        data-theme={theme}
      >
        <div className="text-center max-w-md p-6">
          <div className={cn(
            "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center",
            isDark ? "bg-red-900/50" : "bg-red-100"
          )}>
            <svg className={cn("w-8 h-8", isDark ? "text-red-400" : "text-red-500")} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-slate-900")}>Invalid Access Token</h2>
          <p className={cn("mb-4", isDark ? "text-slate-400" : "text-slate-600")}>
            Your session token is not valid for governance access. 
            Please use the SuperAdmin login.
          </p>
          <a 
            href="/superadmin/login" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-amber-600 text-white h-10 px-4 py-2 hover:bg-amber-500"
          >
            SuperAdmin Login
          </a>
        </div>
      </div>
    );
  }

  // Check if user has governor role (Lockene internal only)
  const isGovernor = user?.role === 'governor' || user?.is_governor === true;
  
  if (!isGovernor) {
    console.log('[SuperAdminRoute] User does not have governor role:', user?.role);
    return (
      <div 
        className={cn(
          "min-h-screen flex items-center justify-center",
          isDark ? "bg-slate-950" : "bg-slate-100"
        )} 
        data-testid="access-denied"
        data-theme={theme}
      >
        <div className="text-center max-w-md p-6">
          <div className={cn(
            "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center",
            isDark ? "bg-red-900/50" : "bg-red-100"
          )}>
            <svg className={cn("w-8 h-8", isDark ? "text-red-400" : "text-red-500")} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-slate-900")}>Access Denied</h2>
          <p className={cn("mb-4", isDark ? "text-slate-400" : "text-slate-600")}>
            You don't have permission to access the AI Governance dashboard. 
            This area is restricted to Lockene internal Governor users only.
          </p>
          <a 
            href="/superadmin/login" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-amber-600 text-white h-10 px-4 py-2 hover:bg-amber-500"
          >
            Login as Governor
          </a>
        </div>
      </div>
    );
  }

  // User is authenticated with governance scope and has governor role
  console.log('[SuperAdminRoute] Governance access granted:', user?.email);
  return children;
};
